import { enableProdMode, LOCALE_ID, importProvidersFrom, Injectable, ErrorHandler, inject, provideAppInitializer } from '@angular/core';
import { environment } from './environments/environment';
import { bootstrapApplication, Title } from '@angular/platform-browser';
import { AppComponent } from './app/app.component';
import { provideRouterStore, routerReducer } from '@ngrx/router-store';
import { GSMissingTranslationHandler } from './app/@shared/missingTranslationHandler.class';
import { provideAnimations } from '@angular/platform-browser/animations';
import { CommonModule } from '@angular/common';
import { appRoutes } from './app/app.routes';
import {
  provideRouter,
  Router,
  RouterStateSnapshot,
  TitleStrategy, withComponentInputBinding,
  withInMemoryScrolling
} from "@angular/router";
import {
  validateAuth,
  validateAuthSuccess,
  validateAuthFailure,
  noSessionFound,
} from './app/@store/auth/auth.actions';
import * as CurrentUserSelectors from './app/@store/current-user/current-user.selectors';
import { DELANG } from './app/lang/de';
import { ENLANG } from './app/lang/en';
import { SkinningService } from '@gridscale/ingrid/helper/services/skinning.service';
import { ColorScheme } from '@gridscale/gs-services/skinning.lib';
import { UiTranslateService } from '@gridscale/ingrid/helper/lang/ui-translate.service';
import { provideState, provideStore, Store } from '@ngrx/store';
import { NgrxService } from './app/@shared/services/ngrx.service';
import {
  TranslateService,
  TranslateModule,
  MissingTranslationHandler,
} from '@ngx-translate/core';
import { GsHttpInterceptor } from './app/@shared/services/gs-http.interceptor';
import { HTTP_INTERCEPTORS, HttpClient, withInterceptorsFromDi, provideHttpClient } from '@angular/common/http';
import * as moment from '@js/moment-lite';
import { provideEffects } from '@ngrx/effects';
import { AccountEffects } from './app/@store/account/account.effects';
import { UserEffects } from './app/@store/user/user.effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { BillingEffects } from './app/@store/billing/billing.effects';
import { LocationEffects } from './app/@store/location/location.effects';
import { BackupLocationEffects } from './app/@store/backup-locations/backup-locations.effects';
import { PartnerEffects } from './app/@store/partner/partner.effects';
import { PartnerStoreEffects } from './app/@store/partnerStore/partnerStore.effects';
import { PriceListsV2Effects } from './app/@store/priceList/priceListv2.effects';
import { PricesEffects } from './app/@store/prices/prices.effects';
import { NewAccountEffects } from './app/@store/new-account/new-account.effects';
import { StatsEffects } from './app/@store/stats/stats.effects';
import { FeatureFlagEffects } from './app/@store/featureFlags/featureFlags.effects';
import { AuthEffects } from './app/@store/auth/auth.effects';
import { HybridCoreEffects } from './app/@store/hybrid-core/hybrid-core.effects';
import * as fromAccount from './app/@store/account/account.reducer';
import * as fromBilling from './app/@store/billing/billing.reducer';
import * as fromPaymentMethod from './app/@store/paymentMethod/paymentMethod.reducer';
import * as fromBackupLocation from './app/@store/backup-locations/backup-locations.reducer';
import * as fromLocation from './app/@store/location/location.reducer';
import * as fromUser from './app/@store/user/user.reducer';
import * as fromPartner from './app/@store/partner/partner.reducer';
import * as fromPartnerStore from './app/@store/partnerStore/partnerStore.reducer';
import * as fromAuth from './app/@store/auth/auth.reducer';
import * as fromPriceListV2 from './app/@store/priceList/priceListv2.reducer';
import * as fromPrices from './app/@store/prices/prices.reducer';
import * as fromNewContract from './app/@store/new-account/new-account.reducer';
import * as fromStats from './app/@store/stats/stats.reducer';
import * as fromFeatureFlag from './app/@store/featureFlags/featureFlags.reducer';
import * as fromHybridCore from './app/@store/hybrid-core/hybrid-core.reducer';
import * as fromMarketplace from './app/@store/marketplace/marketplace.reducer';
import * as fromDeletedContract from './app/@store/deleted-account/deleted-account.reducer';
import { DeletedAccountEffects } from './app/@store/deleted-account/deleted-account.effects';
import * as fromFraudcheck from './app/@store/fraudcheck/fraudcheck.reducer';
import { FraudcheckEffects } from './app/@store/fraudcheck/fraudcheck.effects';
import { MarketplaceEffects } from './app/@store/marketplace/marketplace.effects';
import * as fromLimits from './app/@store/limits/limits.reducer';
import { LimitsEffects } from './app/@store/limits/limits.effects';
import * as fromBranding from './app/@store/branding/branding.reducer';
import { BrandingEffects } from './app/@store/branding/branding.effects';
import * as fromSaml from './app/@store/saml/saml.reducer';
import { SamlEffects } from './app/@store/saml/saml.effects';
import * as Sentry from '@sentry/angular';
import * as _ from 'lodash';
import { COMMIT_HASH } from './commithash';
import { LocalstorageService } from '@gridscale/gs-services/localstorage.service';
import { ConfigService } from '@gridscale/ingrid/helper/services/config.service';
import { routerCustomReducer } from './app/@store/router/router.reducer';


import * as fromOtp from './app/@store/otp/otp.reducer';
import * as fromVirtualSwitches from './app/@store/virtual_switches/virtual_switches.reducer';
import * as fromBreakouts from './app/@store/breakouts/breakouts.reducer';
import * as fromLinks from './app/@store/links/links.reducer';
import * as fromPassword from './app/@store/passwords/password.reducer';

import { reducer as currentUserReducer } from './app/@store/current-user/current-user.reducer';
import { CurrentUserEffects } from './app/@store/current-user/current-user.effects';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { filter } from 'rxjs/operators';
import { OtpEffects } from './app/@store/otp/otp.effects';
import { PasswordsEffects } from './app/@store/passwords/password.effects';
import { VirtualSwitchesEffects } from './app/@store/virtual_switches/virtual_switches.effects';
import { BreakoutsEffects } from './app/@store/breakouts/breakouts.effects';
import { LinksEffects } from './app/@store/links/links.effects';
import { UserStoreService } from './app/@store/userstore/userstore.service';
import { PaymentMethodEffects } from './app/@store/paymentMethod/paymentMethod.effects';
import { MajaConfigService } from '@gridscale/ingrid/helper/services';

interface CleanSession {
  token: string;
  partner_uuid: string;
  project_uuid: string;
  contract_uuid: string;
  user_uuid: string;
}

Sentry.init({
  enabled:
    document.location.hostname !== 'localhost' &&
    environment.sentry_logging === true &&
    !!environment.sentry_endpoint,
  dsn: environment.sentry_endpoint,
  environment: environment.production
    ? window.location.host.match(/\.gridscale\.dev/) ||
      window.location.host.match(/\.nip\.io/)
      ? 'staging'
      : 'production'
    : 'development',
  release: 'partner-panel-' + COMMIT_HASH,
  maxBreadcrumbs: 100,
  // This sets the sample rate to be 10%. You may want this to be 100% while
  // in development and sample at a lower rate in production
  integrations: [
    Sentry.globalHandlersIntegration({
      onerror: true,
      onunhandledrejection: false,
    }),
  ],
  ignoreErrors: ['Non-Error exception captured', 'Uncaught(in promise)'],
  beforeSend(event: any) {
    // we skip StoreServiceErrors which came from an API cause this error were reported on another place (api.addLogger in this file)
    if (
      _.has(event, 'exception.values') &&
      event.exception.values.length &&
      _.has(event.exception.values[0], 'type') &&
      _.has(event, 'extra.error.id')
    ) {
      if (
        event.exception.values[0].type == 'StoreServiceError' &&
        event.extra.error.id.match(/^apierror_/)
      ) {
        return null;
      }
    }

    return event;
  },
});

if (environment.production) {
  enableProdMode();
}

@Injectable()
export class TemplatePageTitleStrategy extends TitleStrategy {
  constructor(
    private readonly title: Title,
    private readonly translate: TranslateService
  ) {
    super();
  }

  override updateTitle(routerState: RouterStateSnapshot) {
    const title = this.buildTitle(routerState);

    if (title !== undefined) {
      this.title.setTitle('Partner Panel - ' + this.translate.instant(title));
    } else {
      this.title.setTitle('Partner Panel');
    }
  }
}

/**
 * APP_INITIALIZER is used to set up the language
 */
export function initApp(
  translate: TranslateService,
  ngrxService: NgrxService,
  store: Store,
  httpClient: HttpClient,
  uiTranslate: UiTranslateService,
  skinningService: SkinningService,
  userStoreService: UserStoreService,
  localStorage: LocalstorageService,
  configService: ConfigService,
  majaConfigService: MajaConfigService,
  router: Router
) {
  return () =>
    new Promise(async (resolve) => {
      skinningService.stylePropertyTargetElement$.next(document.documentElement); // this will automatically apply the css vars to the html element

      const languageInitDone = new BehaviorSubject(false);
      const authDone = new BehaviorSubject(false);

      // Language stuff
      const lang = translate.getBrowserLang() || translate.defaultLang;
      translate.setTranslation('en', ENLANG);
      translate.setTranslation('de', DELANG);
      translate.setDefaultLang(lang);
      translate.onLangChange.subscribe(() => {
        try {
          uiTranslate.currentLang = translate.currentLang;
        } catch (e) {
          console.error('setting ingrid language', e);
        }
      });

      uiTranslate.currentLang = translate.currentLang;


      // load the config endpoint
      let proxyUrl = '';
      if (environment.useCorsProxy && environment.corsProxyUrl) {
        proxyUrl = environment.corsProxyUrl.replace(/\/?$/, '/');
      }
      try {
        let urlPrefix = environment.apiUrl;

        // BUG-5267: Ticket validation foo still runs at partner.gridscale.io which is a special case
        if (document.location.origin.includes('partner.gridscale.io')) {
          urlPrefix = 'https://my.gridscale.io/api';
        }

        await configService.loadApiConfig(proxyUrl + urlPrefix, false);
        try {
          await majaConfigService.loadApiConfig(environment.majaUrl);
        } catch (eMaja) {
          /* we don't care at the moment */
        }


        if (document.location.pathname.indexOf('/System/Error') === 0) {
          router.navigate(['/']);
        }

      } catch (e) {
        resolve(true);
        let queryParams: any;
        if ((window as any)['Cypress'] !== undefined) {
          queryParams = {
            debug: btoa(JSON.stringify({
              url: proxyUrl + environment.apiUrl,
              error: e
            }))
          }
        }

        router.navigate(['/System/Error'], {
          queryParams
        });

        const loader = document.getElementById('appNotLoadedLoader'); // this loader needs to get remove as soon as the app is loaded
        loader?.parentNode?.removeChild(loader);

        return;
      }




      setTimeout(() => {
        store.select(CurrentUserSelectors.user).subscribe((user) => {
          if (user?.language) {
            translate.use(user.language);
            moment.locale(user.language);

            try {
              uiTranslate.currentLang = user.language;
            } catch (e) {
              console.error('setting ingrid language', e);
            }
          }

          languageInitDone.next(true);
        });
      }, 1000);

      const getUrlParameter = function getUrlParameter(sParam: string) {
        const sPageURL = decodeURIComponent(
            window.location.search.substring(1)
          ),
          sURLVariables = sPageURL.split('&');
        let sParameterName, i;

        for (i = 0; i < sURLVariables.length; i++) {
          sParameterName = sURLVariables[i].split('=');

          if (sParameterName[0] === sParam) {
            return sParameterName[1] === undefined ? true : sParameterName[1];
          }
        }
      };

      const authSuccess = (auth: CleanSession) => {
        ngrxService.callBasicActionWithCallbacks({
          actionToCall: validateAuth({ data: auth }),
          successAction: validateAuthSuccess,
          failureAction: validateAuthFailure,
          successCallback: () => {
            authDone.next(true);
          },
          failureCallback: () => {
            authDone.next(true);
          },
        });
      };

      const authFailed = () => {
        store.dispatch(noSessionFound());
        authDone.next(true);
      };

      const defaultSkinValues = skinningService.getStylePropertiesDefault();
      skinningService.setStyleProperties({
        ...defaultSkinValues,
      }, document.documentElement);
      skinningService.overrideProperties$.next({
        logo_data: 'url("assets/img/theme/logo_2x.png")',
        logo_width: '216px',
      });

      userStoreService.subscribe('color_scheme', 'light').subscribe(color_scheme => {
        skinningService.colorScheme.set(color_scheme as ColorScheme);
      });

      const localSession = localStorage.getItem('gs_pp_auth');
      const url = configService.partnerLegacyApi;


      if (getUrlParameter('oneTimeToken')) {
        httpClient
          .get<{
            api_token: string;
            partner_uuid: string;
            project_uuid: string;
            contract_uuid: string;
            user_uuid: string;
          }>(
            url +
              '/api/one-time-token-storage/' +
              getUrlParameter('oneTimeToken')
          )
          .subscribe({
            next: (session) => {
              const cleanSession: CleanSession = {
                token: session.api_token,
                partner_uuid: session.partner_uuid,
                project_uuid: session.project_uuid,
                contract_uuid: session.contract_uuid,
                user_uuid: session.user_uuid,
              };

              // Write Session
              localStorage.setItem('gs_pp_auth', JSON.stringify(cleanSession));
              // Validate
              authSuccess(cleanSession);
            },
            error: (err) => authFailed(),
          });
      } else if (localSession) {
        try {
          const auth = JSON.parse(localSession);
          authSuccess(auth);
        } catch (error) {}
      } else {
        authFailed();
      }

      // Check and finish init
      combineLatest([languageInitDone, authDone])
        .pipe(
          filter(([languageInitDone, authDone]) => languageInitDone && authDone)
        )
        .subscribe(() => {
          const loader = document.getElementById('appNotLoadedLoader'); // this loader needs to get remove as soon as the app is loaded
          loader?.parentNode?.removeChild(loader);
          resolve(true);
        });
    });
}

bootstrapApplication(AppComponent, {
  providers: [
    { provide: 'googleTagManagerId', useValue: 'GTM-5CL692Z' },
    {
      provide: 'googleTagManagerResourcePath',
      useValue: 'https://gtm.gridscale.io/gtm.js',
    },
    importProvidersFrom(
      CommonModule,
      TranslateModule.forRoot({
        missingTranslationHandler: {
          provide: MissingTranslationHandler,
          useClass: GSMissingTranslationHandler,
          deps: [Store],
        },
      }),
    ),
    provideStore(),
    // we provide all store effects used in more than one lazy loaded module, all the other effects were provided in the router configuration of the modules
    provideEffects([
      AuthEffects,
      BillingEffects,
      PaymentMethodEffects,
      AccountEffects,
      CurrentUserEffects,
      DeletedAccountEffects,
      FeatureFlagEffects,
      FraudcheckEffects,
      HybridCoreEffects,
      LimitsEffects,
      BackupLocationEffects,
      LocationEffects,
      MarketplaceEffects,
      NewAccountEffects,
      PartnerEffects,
      PartnerStoreEffects,
      PriceListsV2Effects,
      PricesEffects,
      StatsEffects,
      UserEffects,
      BrandingEffects,
      SamlEffects,
      OtpEffects,
      PasswordsEffects,
      VirtualSwitchesEffects,
      BreakoutsEffects,
      LinksEffects
    ]),
    provideStore({ router: routerReducer, routerCustom: routerCustomReducer }),
    provideRouterStore(),
    // we provide all store states used in more than one lazy loaded module, all the other states were provided in the router configuration of the modules
    provideState(fromAuth.AuthFeatureKey, fromAuth.reducer),
    provideState(fromBilling.billingFeatureKey, fromBilling.reducer),
    provideState(fromPaymentMethod.paymentMethodFeatureKey, fromPaymentMethod.reducer),
    provideState(fromAccount.accountFeatureKey, fromAccount.reducer),
    provideState(
      fromDeletedContract.deletedContractFeatureKey,
      fromDeletedContract.reducer
    ),
    provideState(
      fromFeatureFlag.featureFlagTemplatesFeatureKey,
      fromFeatureFlag.reducer
    ),
    provideState(
      fromFraudcheck.newFraudcheckFeatureKey,
      fromFraudcheck.reducer
    ),
    provideState(fromHybridCore.hybridCoreFeatureKey, fromHybridCore.reducer),
    provideState(fromLimits.limitsFeatureKey, fromLimits.reducer),
    provideState(fromBackupLocation.backupLocationFeatureKey, fromBackupLocation.reducer),
    provideState(fromLocation.locationFeatureKey, fromLocation.reducer),
    provideState(
      fromMarketplace.marketplaceFeatureKey,
      fromMarketplace.reducer
    ),
    provideState(
      fromNewContract.newContractFeatureKey,
      fromNewContract.reducer
    ),
    provideState(fromPartner.partnerFeatureKey, fromPartner.reducer),
    provideState(fromPartnerStore.featureKey, fromPartnerStore.reducer),
    provideState(fromPriceListV2.priceListFeatureKey, fromPriceListV2.reducer),
    provideState(fromPrices.priceFeatureKey, fromPrices.reducer),
    provideState(fromStats.statsFeatureKey, fromStats.reducer),
    provideState(fromUser.userFeatureKey, fromUser.reducer),
    provideState(fromBranding.featureKey, fromBranding.reducer),
    provideState(fromSaml.samlFeatureKey, fromSaml.reducer),
    provideState(fromOtp.otpFeatureKey, fromOtp.reducer),
    provideState(fromVirtualSwitches.featureKey, fromVirtualSwitches.reducer),
    provideState(fromBreakouts.featureKey, fromBreakouts.reducer),
    provideState(fromLinks.featureKey, fromLinks.reducer),
    provideState(fromPassword.passwordFeatureKey, fromPassword.reducer),
    provideState('currentUser', currentUserReducer),
    provideStoreDevtools({
      maxAge: 50,
      logOnly: environment.production,
    }),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    { provide: HTTP_INTERCEPTORS, useClass: GsHttpInterceptor, multi: true },
    { provide: LOCALE_ID, useValue: 'de' },
    provideAppInitializer(() => {
        const initializerFn = (initApp)(inject(TranslateService), inject(NgrxService), inject(Store), inject(HttpClient), inject(UiTranslateService), inject(SkinningService), inject(UserStoreService), inject(LocalstorageService), inject(ConfigService), inject(MajaConfigService), inject(Router));
        return initializerFn();
      }),
    {
      provide: TitleStrategy,
      useClass: TemplatePageTitleStrategy,
    },
    provideRouter(
      appRoutes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
      }),
      withComponentInputBinding()
    ),
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
  ],
}).catch((err) => console.error(err));
